import type { Xof } from './money'

export type EnvelopeId = string

/**
 * Type d'enveloppe (spec §3.3). Il fixe les règles par défaut, que l'utilisateur peut ajuster.
 * - essential : loyer, factures indispensables. Protégée, aucun transfert sortant.
 * - variable : alimentation, eau, transport quotidien.
 * - discretionary : loisirs, sorties, voyages. Source de transfert naturelle.
 * - savings : épargne, objectifs. Protégée, retraits explicites uniquement.
 * - custom : créée librement par l'utilisateur.
 */
export type EnvelopeKind = 'essential' | 'variable' | 'discretionary' | 'savings' | 'custom'

/**
 * Sort du disponible à la clôture d'une période (spec §5.1) :
 * - carry : il reste dans l'enveloppe ;
 * - return : il retourne dans « Non affecté » de la période suivante ;
 * - ask : l'utilisateur choisit au moment de la clôture.
 * L'argent ne disparaît jamais silencieusement.
 */
export type RolloverPolicy = 'carry' | 'return' | 'ask'

/** `all` : toutes les enveloppes. Sinon, la liste explicite des enveloppes autorisées. */
export type EnvelopeScope = 'all' | readonly EnvelopeId[]

export interface TransferRules {
  /** L'enveloppe peut-elle envoyer des fonds vers d'autres enveloppes ? */
  allowOutgoing: boolean
  /** L'enveloppe peut-elle recevoir des fonds d'autres enveloppes ? */
  allowIncoming: boolean
  /** Destinations autorisées quand `allowOutgoing` est vrai. */
  destinations: EnvelopeScope
  /** Sources autorisées quand `allowIncoming` est vrai. */
  sources: EnvelopeScope
  /** Un transfert sortant demande une vérification (PIN ou biométrie). */
  requireVerification: boolean
}

export interface Envelope {
  id: EnvelopeId
  name: string
  icon: string
  kind: EnvelopeKind
  isProtected: boolean
  /** Montant cible mensuel, `null` si l'enveloppe n'en a pas. */
  monthlyTarget: Xof | null
  rolloverPolicy: RolloverPolicy
  transferRules: TransferRules
  /** Autorise un disponible négatif, avec avertissement et trace (spec §5.5). */
  allowOverdraft: boolean
  sortOrder: number
  /** Date d'archivage (ISO), `null` si l'enveloppe est active. */
  archivedAt: string | null
}

export type EnvelopeDefaults = Pick<
  Envelope,
  'isProtected' | 'rolloverPolicy' | 'transferRules' | 'allowOverdraft'
>

const OPEN_RULES: TransferRules = {
  allowOutgoing: true,
  allowIncoming: true,
  destinations: 'all',
  sources: 'all',
  requireVerification: false,
}

/** Règles par défaut de chaque type (spec §5.6). */
export function defaultsForKind(kind: EnvelopeKind): EnvelopeDefaults {
  switch (kind) {
    case 'essential':
      return {
        isProtected: true,
        rolloverPolicy: 'carry',
        allowOverdraft: false,
        transferRules: { ...OPEN_RULES, allowOutgoing: false, requireVerification: true },
      }
    case 'savings':
      return {
        isProtected: true,
        rolloverPolicy: 'carry',
        allowOverdraft: false,
        transferRules: { ...OPEN_RULES, allowOutgoing: false, requireVerification: true },
      }
    case 'variable':
    case 'discretionary':
    case 'custom':
      return {
        isProtected: false,
        rolloverPolicy: 'ask',
        allowOverdraft: false,
        transferRules: { ...OPEN_RULES },
      }
  }
}

export function isActive(envelope: Envelope): boolean {
  return envelope.archivedAt === null
}

export function scopeIncludes(scope: EnvelopeScope, id: EnvelopeId): boolean {
  return scope === 'all' || scope.includes(id)
}
